import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Message } from 'components/Message';
import { Appearance, lineHeight, theme, toRem } from 'design';
import { useMediaQuery } from 'hooks';
import { togglePreventScroll } from 'utils';
import { H3, Text } from '../../Typography';

export interface ISidebarNotification {
  title: string;
  message: string | ReactNode;
  appearance: Appearance.WARNING | Appearance.DANGER;
  preventSubmit: boolean;
}

interface IActionButtonProps {
  label: string;
  appearance: 'primary' | 'white';
  disabled?: boolean;
  onClick: () => void;
}

interface ISidebarProps {
  children?: ReactNode;
  title: string;
  titleValue?: string;
  notifications?: ISidebarNotification[];
  dataCy?: string;
  leftActionButton?: IActionButtonProps;
  rightActionButton?: IActionButtonProps;
}

interface ILayoutProps {
  $collapsedHeightPx: number;
  $isExpanded: boolean;
}

const MAX_EXPANDED_HEIGHT = '70vh';

const Layout = styled.aside<ILayoutProps>`
  position: fixed;
  z-index: ${theme.zIndex.sidebar};
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${toRem(theme.space.sm)};
  width: ${theme.sidebar.width.small};
  background-color: ${theme.color.neutral[50]};
  transition: max-height ${theme.transition.nav};
  top: auto;
  right: 0;
  bottom: 0;
  height: auto;
  max-height: ${({ $isExpanded, $collapsedHeightPx }) => ($isExpanded ? MAX_EXPANDED_HEIGHT : `${$collapsedHeightPx}px`)};
  border-radius: ${toRem(22)} ${toRem(22)} 0 0;
  overflow: hidden;
  box-shadow:
    0px -9px 49px rgba(0, 0, 0, 0.05),
    0px -2.71324px 14.7721px rgba(0, 0, 0, 0.0325794),
    0px -1.12694px 6.13556px rgba(0, 0, 0, 0.025),
    0px -0.407592px 2.21911px rgba(0, 0, 0, 0.0174206);

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 0 0 ${toRem(theme.nav.width.closed)};
    border-radius: 0;
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    top: 0;
    right: 0;
    bottom: 0;
    width: ${toRem(theme.sidebar.width.large)};
    height: 100%;
    padding: ${toRem(theme.header.height + theme.space['3xl'])} 0 0 0;
    border-radius: 0;
    border-left: ${toRem(1)} solid ${theme.color.neutral[200]};
    box-shadow: none;
    gap: ${toRem(theme.space.lg)};
    max-height: none;
  }

  @media (min-width: ${theme.breakpoint.xlarge}px) {
    width: ${toRem(theme.sidebar.width.xlarge)};
  }
`;

const Header = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
  padding: ${toRem(theme.space.lg)} ${toRem(theme.space.lg)} 0 ${toRem(theme.space.lg)};
  flex-shrink: 0;
  justify-content: space-between;

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: 0 ${toRem(theme.space['3xl'])} 0 ${toRem(theme.space['3xl'])};
  }
`;

const ToggleButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ToggleIcon = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform ${theme.transition.nav};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: ${toRem(theme.space.xs)};
`;

const Title = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${toRem(theme.space.xs)};
  width: 100%;

  ${H3} {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
    font-weight: ${theme.font.weight.semiBold};
    color: ${theme.color.neutral[900]};

    @media (min-width: ${theme.breakpoint.large}px) {
      font-size: ${toRem(theme.font.size['3xl'])};
      line-height: ${lineHeight(theme.font.size['3xl'])};
    }
  }
`;

const Content = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  display: ${({ $isExpanded }) => ($isExpanded ? 'flex' : 'none')};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  min-height: auto;
  padding: ${toRem(theme.space.lg)};
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: 0 ${toRem(theme.space['3xl'])} 0 ${toRem(theme.space['3xl'])};
  }
`;

const MobileNotificationBar = styled.div<{ $isExpanded: boolean; $appearance: Appearance.WARNING | Appearance.DANGER }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.sm)};
  background-color: ${theme.color.warning[25]};
  border-radius: ${toRem(8)};
  display: ${({ $isExpanded }) => ($isExpanded ? 'none' : 'flex')};

  ${({ $appearance }) =>
    $appearance === Appearance.WARNING &&
    css`
      background-color: ${theme.color.warning[25]};
    `}

  ${({ $appearance }) =>
    $appearance === Appearance.DANGER &&
    css`
      background-color: ${theme.color.danger[25]};
    `}
`;

const Notifications = styled.div<{ $hasNotifications: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
  margin-top: ${toRem(theme.space.xl)};
  display: ${({ $hasNotifications }) => ($hasNotifications ? 'flex' : 'none')};
`;

const Footer = styled.footer`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.lg)};
  padding: ${toRem(theme.space.lg)};
  flex-shrink: 0;

  button {
    flex-grow: 1;
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: ${toRem(theme.space['3xl'])};
  }
`;

const Sidebar = ({ title, titleValue, notifications, children, leftActionButton, rightActionButton, dataCy = 'sidebar' }: ISidebarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoint.large}px)`);
  const layoutRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  const [collapsedHeightPx, setCollapsedHeightPx] = useState<number>(theme.header.height);

  const isExpanded = isDesktop || isOpen;
  const contentId = useMemo(() => `${dataCy}-content`, [dataCy]);

  useEffect(() => {
    const headerEl = headerRef.current;
    const footerEl = footerRef.current;
    const layoutEl = layoutRef.current;
    if (!headerEl || !footerEl || !layoutEl) return undefined;

    const update = () => {
      const headerHeight = headerEl.getBoundingClientRect().height;
      const footerHeight = footerEl.getBoundingClientRect().height;
      const styles = window.getComputedStyle(layoutEl);
      const gapValue = styles.rowGap || styles.gap || '0px';
      const gapPx = Number.parseFloat(gapValue) || 0;

      // header + gap + footer (content is display:none when collapsed)
      setCollapsedHeightPx(headerHeight + gapPx + footerHeight);
    };

    update();

    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(update);
      ro.observe(headerEl);
      ro.observe(footerEl);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    // Prevent body scroll when sidebar is open on mobile
    if (!isDesktop) togglePreventScroll(isOpen);
    return () => togglePreventScroll(false);
  }, [isOpen, isDesktop]);

  const toggleOpen = useCallback(() => {
    if (isDesktop) return;
    setIsOpen((prev) => !prev);
  }, [isDesktop]);

  return (
    <Layout ref={layoutRef} $collapsedHeightPx={collapsedHeightPx} $isExpanded={isExpanded} data-cy={dataCy}>
      <Header ref={headerRef} data-cy={`${dataCy}-header`}>
        <TitleWrapper>
          {!isDesktop && (
            <ToggleButton type="button" data-cy={`${dataCy}-toggle-button`} aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} aria-controls={contentId} aria-expanded={isExpanded} onClick={toggleOpen}>
              <ToggleIcon $isExpanded={isExpanded}>
                <Icon icon="Dropdown" width={20} height={20} fill={theme.color.neutral[900]} />
              </ToggleIcon>
            </ToggleButton>
          )}
          <Title data-cy={`${dataCy}-title`}>
            {title && <H3>{title}</H3>}
            {titleValue && <H3>{titleValue}</H3>}
          </Title>
        </TitleWrapper>
        {notifications?.length > 0 && (
          <MobileNotificationBar
            data-cy={`${dataCy}-mobile-notification-bar`}
            $isExpanded={isExpanded}
            $appearance={notifications?.some((notification) => notification.appearance === Appearance.DANGER) ? Appearance.DANGER : Appearance.WARNING}
          >
            <Text size="small" fontWeight="semibold">{`${notifications?.length} Issue${notifications?.length > 1 ? 's' : ''} Detected`}</Text>
          </MobileNotificationBar>
        )}
      </Header>
      <Content $isExpanded={isExpanded} aria-hidden={!isExpanded} data-cy={`${dataCy}-content`}>
        {children}
        <Notifications $hasNotifications={notifications?.length > 0}>
          {notifications?.map((notification, index) => (
            <Message key={`notification-${index}`} title={notification.title} message={notification.message} appearance={notification.appearance} />
          ))}
        </Notifications>
      </Content>
      <Footer ref={footerRef} data-cy={`${dataCy}-footer`}>
        {leftActionButton && (
          <Button
            label={leftActionButton.label}
            appearance={leftActionButton.appearance}
            size={isDesktop ? 'large' : 'small'}
            onClick={leftActionButton.onClick}
            disabled={leftActionButton.disabled}
            data-cy={`${dataCy}-left-action-button`}
          />
        )}
        {rightActionButton && (
          <Button
            label={rightActionButton.label}
            appearance={rightActionButton.appearance}
            size={isDesktop ? 'large' : 'small'}
            onClick={rightActionButton.onClick}
            disabled={rightActionButton.disabled}
            data-cy={`${dataCy}-right-action-button`}
          />
        )}
      </Footer>
    </Layout>
  );
};

export default Sidebar;
